// Datenquellen: Typen (Spiegel von apps/core/src/contracts/datasources.ts) und Abfragen.
import { dataSourceRefs, isDataBinding, type DataSet } from '@disploraq/layouts';
import { useQueries } from '@tanstack/react-query';
import { useMemo } from 'react';
import { api, request } from './client';
import { can, useMe, useTenantQuery } from './queries';

export type DataSourceKind = 'feed' | 'calendar' | 'table';
export type DataSourceProvider = 'generic' | 'google_calendar' | 'google_sheets';

export interface DataSourceOptions {
  images?: boolean;
  days?: number;
  showPrivate?: boolean;
  timeZone?: string;
  header?: boolean;
  sheet?: string;
}

export interface Snapshot {
  id: string; versionNo: number; sha256: string; sizeBytes: number; itemCount: number; origin: 'fetch' | 'upload'; fileName: string | null;
  createdAt: string; current: boolean;
}

export interface DataSource {
  id: string; name: string; kind: DataSourceKind; origin: 'url' | 'upload'; provider: DataSourceProvider; url: string | null; urlMasked: boolean;
  intervalMinutes: number; options: DataSourceOptions; enabled: boolean; status: 'pending' | 'ok' | 'error';
  lastAttemptAt: string | null; lastSuccessAt: string | null; lastChangeAt: string | null; lastError: string | null; lastErrorCode: string | null;
  consecutiveFailures: number; nextFetchAt: string | null; current: Snapshot | null; rowVersion: number; createdAt: string; updatedAt: string;
}
export interface DataSourceDetail extends DataSource { usedBy: { type: 'layout' | 'playlist'; id: string; name: string }[] }
export interface DataSourceData { sourceId: string; kind: DataSourceKind; versionNo: number; sha256: string; fetchedAt: string; data: DataSet }
export interface TestResult { ok: boolean; provider: DataSourceProvider; itemCount: number; data: DataSet | null; error: { code: string; message: string } | null }

export const qkDs = {
  list: ['datasources'] as const,
  one: (id: string) => ['datasources', id] as const,
  data: (id: string) => ['datasources', id, 'data'] as const,
  versions: (id: string) => ['datasources', id, 'versions'] as const,
};

export const useDataSources = (opts: { enabled?: boolean } = {}) => {
  const { data: me } = useMe();
  return useTenantQuery(qkDs.list, async () => (await api.get<{ items: DataSource[] }>('/datasources')).items, {
    enabled: can(me, 'datasources.read') && (opts.enabled ?? true),
    // Abrufe laufen im Hintergrund: Status alle 10 s (ausstehende Quellen alle 3 s)
    refetchInterval: (d) => (d?.some((s) => s.status === 'pending' && s.enabled) ? 3000 : 10_000),
  });
};
export const useDataSource = (id: string | null) => useTenantQuery(qkDs.one(id ?? ''), () => api.get<DataSourceDetail>(`/datasources/${id}`), { enabled: !!id });
export const useDataSourceData = (id: string | null) => useTenantQuery(qkDs.data(id ?? ''), async () => {
  try {
    return await api.get<DataSourceData>(`/datasources/${id}/data`);
  } catch {
    return null;
  }
}, { enabled: !!id });
export const useVersions = (id: string | null) => useTenantQuery(qkDs.versions(id ?? ''), async () => (await api.get<{ items: Snapshot[] }>(`/datasources/${id}/versions`)).items, { enabled: !!id });

/** Datei (CSV/XLSX) als neue Version hochladen. */
export function uploadTable(id: string, file: File): Promise<DataSource> {
  const type = /\.xlsx$/i.test(file.name) ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' : 'text/csv';
  return request<DataSource>('POST', `/datasources/${id}/upload?fileName=${encodeURIComponent(file.name)}`, { raw: file, headers: { 'Content-Type': type } });
}

export const imageUrl = (sha: string) => `/api/v1/datasources/images/${sha}`;

/** Datenquellen, die eine Folie verwendet (Bausteine der Definition und Feldbelegungen). */
export function usedSources(definition: unknown, content?: unknown): string[] {
  const ids = new Set(dataSourceRefs(definition));
  for (const v of Object.values((content ?? {}) as Record<string, unknown>)) if (isDataBinding(v)) ids.add(v.source);
  return [...ids].sort();
}

/** Daten mehrerer Quellen für die Vorschau im Manager (Studio, Folie gestalten, Playlist-Kacheln). */
export function useDataSets(ids: string[]): { get: (id: string) => DataSet | undefined; images: Set<string>; version: string } {
  const { data: me } = useMe();
  const allowed = can(me, 'datasources.read');
  const results = useQueries({
    queries: ids.map((id) => ({
      queryKey: [...qkDs.data(id), me?.activeTenantId ?? 'none'],
      queryFn: async () => {
        try {
          return await api.get<DataSourceData>(`/datasources/${id}/data`);
        } catch {
          return null;
        }
      },
      enabled: allowed && !!me?.activeTenantId,
      staleTime: 30_000,
    })),
  });
  const version = results.map((r) => r.data?.sha256 ?? '-').join(',');
  return useMemo(() => {
    const map = new Map<string, DataSet>();
    const images = new Set<string>();
    results.forEach((r, i) => {
      if (!r.data) return;
      map.set(ids[i]!, r.data.data);
      if (r.data.data.kind === 'feed') for (const it of r.data.data.items) if (it.image) images.add(it.image);
    });
    return { get: (id: string) => map.get(id), images, version };
  }, [version, ids.join(',')]);
}
